import {
  BarChart3,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Contact,
  Gift,
  LayoutDashboard,
  LogOut,
  MenuSquare,
  MessageSquare,
  Package,
  Percent,
  Settings,
  ShoppingBag,
  Truck,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchSettings } from "../settings/settingsApi";
import { fetchAdminOverview } from "../api/adminOverviewApi";
import { logoutUser } from "../../utils/auth";

const navigationItems = [
  {
    section: "Overview",
    items: [
      {
        label: "Dashboard",
        icon: LayoutDashboard,
        path: "/admin",
      },
    ],
  },
  {
    section: "Store",
    items: [
      {
        label: "Orders",
        icon: ShoppingBag,
        path: "/admin/orders",
      },
      {
        label: "Menu",
        icon: MenuSquare,
        path: "/admin/menu",
      },
      {
        label: "Inventory",
        feature: "inventory_enabled",
        icon: Package,
        path: "/admin/inventory",
      },
      {
        label: "Deliveries",
        icon: Truck,
        path: "/admin/deliveries",
      },
      {
        label: "Monthly Specials",
        feature: "monthly_specials_enabled",
        icon: Gift,
        path: "/admin/specials",
      },
      {
        label: "Coupons",
        feature: "coupons_enabled",
        icon: Percent,
        path: "/admin/coupons",
      },
    ],
  },
  {
    section: "Business",
    items: [
      {
        label: "Customers",
        feature: "customer_crm_enabled",
        icon: Contact,
        path: "/admin/customers",
      },
      {
        label: "Messages",
        feature: "messages_enabled",
        icon: MessageSquare,
        path: "/admin/messages",
      },
      {
        label: "Reports",
        feature: "reports_enabled",
        icon: BarChart3,
        path: "/admin/reports",
      },
      {
        label: "Activity",
        icon: ClipboardList,
        path: "/admin/activity",
      },
    ],
  },
];

function AdminSidebar({
  isCollapsed,
  isMobileOpen,
  onCloseMobile,
  onToggleCollapsed,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [featureFlags, setFeatureFlags] = useState({});
  const [badges, setBadges] = useState({});

  useEffect(() => {
    let alive = true;
    fetchSettings().then((settings) => { if (alive) setFeatureFlags(settings?.features || {}); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    async function loadBadges() {
      try {
        const data = await fetchAdminOverview();
        if (alive) setBadges(data.badges || {});
      } catch {
        if (alive) setBadges({});
      }
    }
    loadBadges();
    window.addEventListener("focus", loadBadges);
    return () => {
      alive = false;
      window.removeEventListener("focus", loadBadges);
    };
  }, [location.pathname]);

  function itemBadge(label) {
    if (label === "Orders") return badges.new_orders;
    if (label === "Inventory") return badges.low_stock_items;
    if (label === "Messages") return badges.unread_messages;
    return 0;
  }

  const handleNavigation = (path) => {
    navigate(path);
    onCloseMobile?.();
  };

  const isItemActive = (path) => {
    if (path === "/admin") {
      return location.pathname === "/admin";
    }

    return location.pathname.startsWith(path);
  };

  return (
    <>
      {isMobileOpen && (
        <button
          type="button"
          className="admin-sidebar-backdrop"
          aria-label="Close navigation"
          onClick={onCloseMobile}
        />
      )}

      <aside
        className={[
          "admin-sidebar",
          isCollapsed ? "admin-sidebar--collapsed" : "",
          isMobileOpen ? "admin-sidebar--mobile-open" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className="admin-sidebar__top">
          <button
            type="button"
            className="admin-sidebar__mobile-close"
            aria-label="Close navigation"
            onClick={onCloseMobile}
          >
            <X size={21} />
          </button>

          <button
            type="button"
            className="admin-sidebar__brand"
            onClick={() => handleNavigation("/admin")}
            aria-label="Go to dashboard"
          >
            <div className="admin-sidebar__logo">
              <span>SL</span>
            </div>

            {!isCollapsed && (
              <div className="admin-sidebar__brand-copy">
                <strong>SugaLumps</strong>
                <span>Bakery Admin</span>
              </div>
            )}
          </button>
        </div>

        <nav className="admin-sidebar__navigation">
          {navigationItems.map((group) => (
            <div
              className="admin-sidebar__group"
              key={group.section}
            >
              {!isCollapsed && (
                <p className="admin-sidebar__section-label">
                  {group.section}
                </p>
              )}

              <div className="admin-sidebar__links">
                {group.items.filter((item) => !item.feature || featureFlags[item.feature] !== false).map((item) => {
                  const Icon = item.icon;
                  const isActive = isItemActive(item.path);
                  const badge = itemBadge(item.label);

                  return (
                    <button
                      type="button"
                      key={item.path}
                      className={[
                        "admin-sidebar__link",
                        isActive
                          ? "admin-sidebar__link--active"
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={() =>
                        handleNavigation(item.path)
                      }
                      title={
                        isCollapsed ? item.label : undefined
                      }
                      aria-current={
                        isActive ? "page" : undefined
                      }
                    >
                      <span className="admin-sidebar__link-icon">
                        <Icon size={20} />
                      </span>

                      {!isCollapsed && (
                        <>
                          <span className="admin-sidebar__link-label">
                            {item.label}
                          </span>

                          {badge > 0 ? (
                            <span className="admin-sidebar__badge">
                              {badge > 99 ? "99+" : badge}
                            </span>
                          ) : null}
                        </>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="admin-sidebar__footer">
          <button
            type="button"
            className={[
              "admin-sidebar__link",
              isItemActive("/admin/settings")
                ? "admin-sidebar__link--active"
                : "",
            ]
              .filter(Boolean)
              .join(" ")}
            title={isCollapsed ? "Settings" : undefined}
            onClick={() =>
              handleNavigation("/admin/settings")
            }
          >
            <span className="admin-sidebar__link-icon">
              <Settings size={20} />
            </span>

            {!isCollapsed && (
              <span className="admin-sidebar__link-label">
                Settings
              </span>
            )}
          </button>

          <button
            type="button"
            className="admin-sidebar__link admin-sidebar__link--danger"
            title={isCollapsed ? "Log out" : undefined}
            onClick={() => {
              logoutUser();
              onCloseMobile?.();
              navigate("/login", {
                replace: true,
              });
            }}
          >
            <span className="admin-sidebar__link-icon">
              <LogOut size={20} />
            </span>

            {!isCollapsed && (
              <span className="admin-sidebar__link-label">
                Log out
              </span>
            )}
          </button>
        </div>

        <button
          type="button"
          className="admin-sidebar__collapse-button"
          aria-label={
            isCollapsed
              ? "Expand sidebar"
              : "Collapse sidebar"
          }
          onClick={onToggleCollapsed}
        >
          {isCollapsed ? (
            <ChevronRight size={18} />
          ) : (
            <ChevronLeft size={18} />
          )}
        </button>
      </aside>
    </>
  );
}

export default AdminSidebar;

